/**
 * Defines presentation-only formatting and drag payloads for the Vault browser.
 *
 * These helpers keep layout behavior consistent but intentionally carry no
 * authorization; every dragged, sorted, or labeled item is revalidated by the
 * backend when an operation occurs.
 */
import type { ItemResponse, VaultKind } from '../types'
import { sourceProviderLabel } from './provider-labels'

export const formatSize = (bytes?: number) => {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export const formatDate = (iso: string) => {
  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

export const kindLabel = (entry: ItemResponse) => {
  if (entry.item.metadata.description) return entry.item.metadata.description
  if (entry.item.linked && entry.item.kind === 'folder') {
    // cm-coding: A linked folder describes how Vault projects the item, not
    // where it came from. Name connector ownership only from persisted source
    // provenance; missing provenance must stay neutral instead of attributing
    // a ServiceNow, Box, or native record to the workspace by guesswork.
    if (!entry.item.sourceConnector) return 'Linked folder'
    const source = sourceProviderLabel(entry.item.sourceConnector)
    return entry.item.sourceConnector === 'docreview'
      ? `Live from ${source}`
      : `Live from ${source} connector`
  }
  const kind: VaultKind = entry.item.kind
  if (kind === 'folder') return 'Folder'
  if (kind === 'note') return 'Note'
  return entry.item.mimeType?.split('/')[1]?.toUpperCase().slice(0, 8) || 'File'
}

const METADATA_LABELS: Record<string, string> = {
  'person.first_name': 'First name',
  'person.last_name': 'Last name',
}

export const metadataLabel = (key: string) => METADATA_LABELS[key] ?? key

export const canEdit = (entry: ItemResponse) =>
  !entry.item.readOnly && (entry.effectiveRole === 'editor' || entry.effectiveRole === 'owner')

export interface VaultDragPayload {
  itemId: string
  revision: number
  name: string
  kind: VaultKind
}

const DRAG_TYPE = 'application/x-vault-item'

export const setDragPayload = (event: React.DragEvent, payload: VaultDragPayload) => {
  event.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload))
  event.dataTransfer.effectAllowed = 'move'
}

export const hasDragPayload = (event: React.DragEvent) =>
  event.dataTransfer.types.includes(DRAG_TYPE)

export const hasFilePayload = (event: React.DragEvent) =>
  event.dataTransfer.types.includes('Files')

export const getDragPayload = (event: React.DragEvent): VaultDragPayload | null => {
  const raw = event.dataTransfer.getData(DRAG_TYPE)
  if (!raw) return null
  try {
    return JSON.parse(raw) as VaultDragPayload
  } catch {
    return null
  }
}

const AVATAR_COLORS = [
  'bg-violet-200 text-violet-800',
  'bg-sky-200 text-sky-800',
  'bg-amber-200 text-amber-800',
  'bg-emerald-200 text-emerald-800',
  'bg-rose-200 text-rose-800',
  'bg-indigo-200 text-indigo-800',
]

export const avatarColor = (userId: string) => {
  let hash = 0
  for (const char of userId) hash = (hash * 31 + char.charCodeAt(0)) % 997
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}
